import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * Gestión de roles y permisos del panel (HU-009-A Fase 3) — listar el
 * catálogo de permisos, listar/crear/editar/eliminar roles y reemplazar el
 * set de permisos de un rol. Mismo patrón que `useUsuarios.js`: siempre
 * manda el JWT del personal logueado, la API ya responde 401/403 sin token
 * real (no hace falta un fallback silencioso como en los hooks del
 * ciudadano).
 */
const usePermisos = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    ...extra,
  });

  const manejarRespuesta = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const erroresDeCampo = Object.entries(data)
        .filter(([campo]) => campo !== 'error' && campo !== 'detail')
        .map(([campo, mensajes]) => `${campo}: ${Array.isArray(mensajes) ? mensajes.join(', ') : mensajes}`)
        .join(' — ');
      throw new Error(data.error || data.detail || erroresDeCampo || 'Error inesperado del servidor');
    }
    return data;
  };

  const listarPermisos = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.permiso.listarPermisos, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, permisos: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const listarRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.permiso.listarRoles, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, roles: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const crearRol = async (datos) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.permiso.crearRol, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datos),
      });
      const data = await manejarRespuesta(response);
      return { success: true, rol: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const actualizarRol = async (id, datos) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.permiso.detalleRol(id), {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datos),
      });
      const data = await manejarRespuesta(response);
      return { success: true, rol: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const eliminarRol = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.permiso.detalleRol(id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok && response.status !== 204) {
        await manejarRespuesta(response);
      }
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const actualizarPermisosRol = async (id, permisosIds) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.permiso.actualizarPermisosRol(id), {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ permisos_ids: permisosIds }),
      });
      const data = await manejarRespuesta(response);
      return { success: true, rol: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    listarPermisos,
    listarRoles,
    crearRol,
    actualizarRol,
    eliminarRol,
    actualizarPermisosRol,
  };
};

export default usePermisos;
